import type { NavigationScreenState } from '@gitbook/app-navigation';

import { lazyWithPreload, type Preloadable } from '../navigation';
import { AgentsScreen } from './Agents';
import { ChangeRequestsScreen } from './ChangeRequests';
import { SiteScreen } from './Site';
import { SpaceScreen } from './Space';
import { TriageScreen } from './Triage';

type ScreenType = NavigationScreenState['type'];

/**
 * Stable empty result so screens without a bunch keep a constant identity
 * across renders (the preload effect depends on the returned array).
 */
const NO_NEIGHBORS: Preloadable[] = [];

/**
 * Which screens to warm after landing on a given screen.
 *
 * Grouping follows how people actually move through the product: the quality
 * surfaces (Triage / Change Requests / Agents) cluster together, content
 * surfaces (Site / Space) pair up, and the API surfaces (Integrations /
 * OpenAPI) preload their siblings. Each array is a module constant so its
 * identity is stable across renders.
 */
const SCREEN_BUNCHES: Partial<Record<ScreenType, Preloadable[]>> = {
    triage: [ChangeRequestsScreen, AgentsScreen],
    change_requests: [TriageScreen, AgentsScreen],
    agents: [TriageScreen, ChangeRequestsScreen],
    site: [SpaceScreen, TriageScreen],
    space: [SiteScreen, ChangeRequestsScreen],
    home: [SiteScreen, SpaceScreen],
    sites: [SiteScreen, SpaceScreen],
};

/**
 * Resolve the screens to preload once the given screen has settled. Returns a
 * stable array reference per screen type so callers can safely use it as an
 * effect dependency.
 */
export function getScreenNeighbors(type: ScreenType): Preloadable[] {
    return SCREEN_BUNCHES[type] ?? NO_NEIGHBORS;
}

/**
 * Heavy site tabs are code-split so a first visit only loads the landing
 * (Overview) tab; the rest are fetched on demand and warmed on idle. The
 * `undefined` (Overview) tab and the light Members/Structure tabs stay eager.
 */
export const SiteNewAnalytics = lazyWithPreload(() =>
    import('./Site/components/NewAnalytics').then((m) => ({ default: m.SiteNewAnalytics }))
);
export const SiteSettings = lazyWithPreload(() =>
    import('./Site/components/SiteSettings').then((m) => ({ default: m.SiteSettings }))
);
export const SiteCustomizationScreen = lazyWithPreload(() =>
    import('../Components/SiteCustomizationFlow').then((m) => ({
        default: m.SiteCustomizationScreen,
    }))
);
export const SitePreviewScreen = lazyWithPreload(() =>
    import('../Components/SitePreview').then((m) => ({ default: m.SitePreviewScreen }))
);

/**
 * Tabs to warm on idle after the site screen settles — the ones people open
 * most from Overview. Customization stays load-on-demand: it's the heaviest
 * tab and comparatively rarely visited.
 */
export const SITE_SCREEN_BUNCHES: Preloadable[] = [SiteSettings, SiteNewAnalytics];

/**
 * Mode/branch-specific views are code-split so the common page view doesn't ship
 * the preview and change-request-overview code. Note: the Slate editor lives on
 * the shared read/edit path (`SpaceContent`) and is intentionally left eager —
 * splitting it off the read path is a separate, larger change.
 */
export const SpacePreview = lazyWithPreload(() =>
    import('./Space/Preview').then((m) => ({ default: m.SpacePreview }))
);
export const ChangeRequestOverview = lazyWithPreload(() =>
    import('../Components/ChangeRequest/Overview').then((m) => ({
        default: m.ChangeRequestOverview,
    }))
);

/**
 * Mode/branch-specific views to warm on idle once a space screen settles.
 */
export const SPACE_SCREEN_BUNCHES: Preloadable[] = [SpacePreview, ChangeRequestOverview];
